import React, { useEffect, useState } from "react";
import { apiRequestWithAuth } from "../../utils/api";
import Alert from "../../components/ui/alert/Alert";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import FileInput from "../../components/form/input/FileInput";
import {useNavigate, useParams} from "react-router-dom";
import MyEditor from "../../components/form/input/MyEditor";
import {Textarea} from "@mantine/core";
import Select from "react-tailwindcss-select";
import { SelectValue } from "react-tailwindcss-select/dist/components/type";

type Category = {
    id: number;
    title: string;
};

type Quiz = {
    id: number;
    title: string;
};

const CourseCreatePage = () => {
    const { id } = useParams<{ id: string }>();
    const defaultForm = {
        title: "",
        shortDescription: "",
        postContent: "",
        durationDays: 30,
        categoryId: "",
        visibility: "private",
        image: null,
        regularPrice: '',
        salePrice: '',
        quizIds: [] as number[],
        status: 1,
    };

    const [categories, setCategories] = useState<Category[]>([]);
    const [formData, setFormData] = useState(defaultForm);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const navigate = useNavigate();

    // Quiz select state
    const [quizzes, setQuizzes] = useState<Quiz[]>([]);
    const [selectedQuizzes, setSelectedQuizzes] = useState<SelectValue>([]);
    const [quizSearchTerm, setQuizSearchTerm] = useState("");

    // Fetch quizzes with optional search
    const fetchQuizzes = async (search: string = "") => {
        try {
            const endpoint = search
                ? `/quiz?keyword=${encodeURIComponent(search)}`
                : "/quiz";
            const res = await apiRequestWithAuth(endpoint, "GET", {});
            if (res.messageCode === 200) {
                setQuizzes(res.result || []);
            }
        } catch (error) {
            console.error("Failed to load quizzes:", error);
        }
    };

    // Initial load quizzes
    useEffect(() => {
        fetchQuizzes();
    }, []);

    // Debounce search
    useEffect(() => {
        const delay = setTimeout(() => {
            fetchQuizzes(quizSearchTerm);
        }, 300);
        return () => clearTimeout(delay);
    }, [quizSearchTerm]);

    const handleQuizChange = (value: SelectValue) => {
        setSelectedQuizzes(value);
        const ids = Array.isArray(value)
            ? value.map((item: any) => parseInt(item.value))
            : [];
        setFormData(prev => ({ ...prev, quizIds: ids }));
    };

    const handleRemoveQuiz = (quizId: number) => {
        const newSelected = Array.isArray(selectedQuizzes)
            ? selectedQuizzes.filter((item: any) => parseInt(item.value) !== quizId)
            : [];
        setSelectedQuizzes(newSelected);
        setFormData(prev => ({
            ...prev,
            quizIds: prev.quizIds.filter(qid => qid !== quizId),
        }));
    };

    // Fetch existing course
    useEffect(() => {
        const fetchCourse = async () => {
            try {
                const res = await apiRequestWithAuth(`/course/${id}`, "GET", {});
                const data = res.result;

                // Extract quizIds from quizzes array
                let quizIdsArray: number[] = [];
                if (data.quizzes && Array.isArray(data.quizzes) && data.quizzes.length > 0) {
                    quizIdsArray = data.quizzes.map((q: Quiz) => q.id);
                }

                setFormData({
                    title: data.title,
                    shortDescription: data.shortDescription,
                    postContent: data.content,
                    durationDays: data.durationDays,
                    categoryId: data.categoryId,
                    visibility: data.visibility,
                    image: data.image,
                    regularPrice: data.regularPrice,
                    salePrice: data.salePrice,
                    quizIds: quizIdsArray,
                    status: data.status || 1,
                });

                if (data && data.showImage && data.showImage.publicPath) {
                    setPreview(data.showImage.publicPath);
                }

                const file = data.files?.find((f: any) => f.metaKey === "image");
                if (file && file.publicPath) {
                    setPreview(file.publicPath);
                }

                // Pre-select quizzes từ data.quizzes (giống EditDynamicBlock với data.courses)
                if (data.quizzes && Array.isArray(data.quizzes) && data.quizzes.length > 0) {
                    const preSelected = data.quizzes.map((q: Quiz) => ({
                        value: q.id.toString(),
                        label: q.title,
                    }));
                    setSelectedQuizzes(preSelected);
                }
            } catch (error) {
                console.error("Lỗi khi lấy dữ liệu slider:", error);
            }
        };

        if (id) fetchCourse();
    }, [id]);

    const fetchCategories = async () => {
        try {
            const res = await apiRequestWithAuth("/category", "GET", {});
            setCategories(res.result);
        } catch (error) {
            console.error("Lỗi lấy danh mục:", error);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFormData(prev => ({ ...prev, shortDescription: e.target.value }));
    };

    const handleUploadImage = async (file: File) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("subject", "course_cover");
        try {
            const res = await apiRequestWithAuth('/media/file', 'POST', fd);
            return res.result?.id || null;
        } catch (error) {
            console.error("Upload image failed", error);
            return null;
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setPreview(URL.createObjectURL(file));
            const uploadedId = await handleUploadImage(file);
            setFormData(prev => ({ ...prev, image: uploadedId }));
        }
    };

    const handleSave = async () => {
        try {
            await apiRequestWithAuth(`/course/${id}`, "PUT", formData);
            setOpenSnackbar(true);
            setTimeout(() => navigate("/courses"), 1000);
        } catch (error) {
            console.error("Create course failed", error);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    return (
        <div>
            {openSnackbar && (
                <Alert variant="success" title="Cập nhật khóa học thành công" message="Bạn đã cập nhật khoá học mới thành công." />
            )}

            <PageMeta title="Tạo khóa học" description="Trang tạo mới khóa học" />
            <PageBreadcrumb pageTitle="Tạo khóa học" />

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
                <div className="space-y-6">
                    <ComponentCard title="Thông tin khoá học">
                        <div className="space-y-6">
                            <div>
                                <Label>Tiêu đề</Label>
                                <Input name="title" value={formData.title} onChange={handleChange} />
                            </div>

                            <div>
                                <Label>Mô tả ngắn</Label>
                                <Textarea name="shortDescription" value={formData.shortDescription} onChange={handleDescriptionChange}/>
                            </div>

                            <div>
                                <Label>Ảnh bìa</Label>
                                <FileInput onChange={handleFileChange} />
                                {preview && (
                                    <img src={preview} alt="Preview" className="w-64 h-32 object-cover rounded border mt-2" />
                                )}
                            </div>

                            <div>
                                <Label>Nội dung chi tiết</Label>
                                <MyEditor
                                    content={formData.postContent}
                                    onBlur={(value) => handleChange({ target: { name: "postContent", value } } as any)}
                                />
                            </div>

                            <div>
                                <Label>Số ngày</Label>
                                <Input name="durationDays" type="number" value={formData.durationDays} onChange={handleChange} />
                            </div>

                            <div>
                                <Label>Danh mục</Label>
                                <select name="categoryId" value={formData.categoryId} onChange={handleChange} className="block w-full border border-gray-300 rounded-md shadow-sm p-2">
                                    <option value="">-- Chọn danh mục --</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.title}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <Label>Hiển thị</Label>
                                <select name="visibility" value={formData.visibility} onChange={handleChange} className="block w-full border border-gray-300 rounded-md shadow-sm p-2">
                                    <option value="private">Riêng tư</option>
                                    <option value="public">Công khai</option>
                                </select>
                            </div>

                            <div>
                                <Label>Trạng thái</Label>
                                <select name="status" value={formData.status} onChange={handleChange} className="block w-full border border-gray-300 rounded-md shadow-sm p-2">
                                    <option value="1">Active</option>
                                    <option value="2">Draft</option>
                                    <option value="3">Inactive</option>
                                    <option value="4">Deleted</option>
                                </select>
                            </div>

                            <div>
                                <Label>Giá gốc</Label>
                                <Input name="regularPrice" value={formData.regularPrice} onChange={handleChange} />
                            </div>

                            <div>
                                <Label>Giá khuyến mãi</Label>
                                <Input name="salePrice" value={formData.salePrice} onChange={handleChange} />
                            </div>

                            {/* Quiz Select */}
                            <div className="ctmSelectMulti">
                                <Label>Quiz</Label>
                                <Select
                                    value={selectedQuizzes}
                                    onChange={handleQuizChange}
                                    options={quizzes.map(quiz => ({
                                        value: quiz.id.toString(),
                                        label: quiz.title,
                                    }))}
                                    primaryColor="blue"
                                    isMultiple={true}
                                    isSearchable={true}
                                    placeholder="Chọn quiz..."
                                    searchInputPlaceholder="Tìm kiếm quiz..."
                                    onSearchInputChange={(e) => setQuizSearchTerm(e.target.value)}
                                    formatOptionLabel={(data: any) => (
                                        <li className="block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded hover:bg-blue-100">
                                            {data.label}
                                        </li>
                                    )}
                                    classNames={{
                                        menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-800",
                                        list: "dark:bg-gray-800 !max-h-72 overflow-y-auto",
                                    }}
                                />

                                {Array.isArray(selectedQuizzes) && selectedQuizzes.length > 0 && (
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Các quiz đã chọn:
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedQuizzes.map((quiz: any) => (
                                                <div
                                                    key={quiz.value}
                                                    className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-1.5"
                                                >
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {quiz.label}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveQuiz(parseInt(quiz.value))}
                                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                        title="Xóa quiz"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </ComponentCard>
                </div>

                <button onClick={handleSave} className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300">
                    Lưu khoá học
                </button>
            </div>
        </div>
    );
};

export default CourseCreatePage;
